import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Globe2, Search } from "lucide-react";
import { getZoneOptions, getPinnedZoneOptions, searchZoneOptions, describeInstant } from "../../lib/timeConverter";

// Abbreviation + offset for the selected date (noon UTC avoids most transition edges).
const metaOn = (o, date) => {
    const ms = /^\d{4}-\d{2}-\d{2}$/.test(date || "") ? Date.parse(`${date}T12:00:00Z`) : Date.now();
    const d = describeInstant(ms, o.tz);
    return { abbr: d.abbr || o.abbr, offset: d.offset };
};

// Bundled SVG flags (public/flags) render on every OS; emoji is only the load-failure fallback.
function Flag({ o }) {
    const [failed, setFailed] = useState(false);
    if (!o.code || failed) return <span className="text-base leading-none shrink-0" aria-hidden>{o.flag}</span>;
    return <img src={`/flags/${o.code}.svg`} alt="" aria-hidden width={20} height={14} loading="lazy" onError={() => setFailed(true)}
        data-testid="zone-flag-img" className="w-[21px] h-[14px] shrink-0 rounded-[2px] object-contain ring-1 ring-black/10" />;
}

const Dial = ({ o }) => (o.dialLabel ? <span className="shrink-0 whitespace-pre text-slate-500 font-normal" data-testid="zone-dial"> ({o.dialLabel})</span> : null);

function Row({ o, date, active, selected, onPick, onHover, idx, testId }) {
    const m = metaOn(o, date);
    return (
        <button type="button" role="option" aria-selected={selected} data-idx={idx}
            data-testid={`${testId}-opt-${o.code || "zz"}-${o.tz}`}
            onMouseEnter={onHover} onClick={onPick}
            className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"} ${selected ? "text-[#1E3A8A] font-semibold" : "text-slate-800"}`}>
            <Flag o={o} />
            {o.code && <span className="shrink-0 text-[10px] font-bold font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{o.code}</span>}
            <span className="flex-1 min-w-0 flex"><span className="truncate">{o.name}</span><Dial o={o} /></span>
            {m.abbr && <span className="shrink-0 text-[11px] font-bold text-[#1E3A8A]">{m.abbr}</span>}
            <span className="shrink-0 text-[10px] text-slate-500 tabular-nums hidden sm:inline md:hidden lg:inline">{m.offset}</span>
            {selected && <Check className="w-3.5 h-3.5 shrink-0 text-[#1E3A8A]" />}
        </button>
    );
}

/** Converter-only country / time-zone picker: full list on open, word-by-word search. */
export function ZoneSelect({ value, onChange, date, testId, placeholder }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const rootRef = useRef(null), inputRef = useRef(null), listRef = useRef(null);
    const options = getZoneOptions();
    const selected = options.find((o) => o.value === value) || null;
    // Empty search: pinned key markets first, then every other location (no duplicates).
    const pinned = useMemo(() => (query.trim() ? [] : getPinnedZoneOptions()), [query]);
    const results = useMemo(() => {
        if (query.trim()) return searchZoneOptions(query, options);
        const ids = new Set(pinned.map((o) => o.value));
        return [...pinned, ...options.filter((o) => !ids.has(o.value))];
    }, [query, options, pinned]);

    useEffect(() => { setActive(0); }, [query, open]);
    useEffect(() => {
        if (!open) return;
        setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 20);
        // Bring the whole panel above the fold / fixed bottom bars (~96px) when it would overflow.
        requestAnimationFrame(() => {
            const r = rootRef.current?.getBoundingClientRect();
            if (!r) return;
            const panelH = Math.min(380, window.innerHeight * 0.5 + 90);
            const overflow = r.bottom + panelH - (window.innerHeight - 96);
            if (overflow > 0) window.scrollBy({ top: Math.min(overflow, Math.max(0, r.top - 80)), behavior: "smooth" });
        });
    }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("touchstart", onDoc);
        return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("touchstart", onDoc); };
    }, [open]);
    useEffect(() => {
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [active]);

    const pick = (o) => { onChange(o.value); setOpen(false); setQuery(""); };
    const onKey = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(results.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
        else if (e.key === "Enter") { e.preventDefault(); if (results[active]) pick(results[active]); }
        else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    };

    return (
        <div ref={rootRef} className="relative">
            <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open} data-testid={testId}
                className={`w-full h-11 flex items-center gap-2 px-3 text-left text-sm bg-white border rounded-lg outline-none transition-colors ${open ? "border-[#1E3A8A] ring-1 ring-[#1E3A8A]" : "border-slate-200 hover:border-slate-300"}`}>
                {selected ? (
                    <>
                        <Flag key={selected.value} o={selected} />
                        <span className="flex-1 min-w-0 flex font-semibold text-[#0F172A]" data-testid={`${testId}-selected`}>
                            <span className="truncate">{selected.code ? `${selected.code} — ` : ""}{selected.name}</span><Dial o={selected} />{metaOn(selected, date).abbr && <span className="shrink-0 whitespace-pre">{` (${metaOn(selected, date).abbr})`}</span>}
                        </span>
                        <span className="shrink-0 text-[11px] text-slate-500 tabular-nums">{metaOn(selected, date).offset}</span>
                    </>
                ) : (
                    <><Globe2 className="w-4 h-4 text-slate-400" /><span className="flex-1 text-slate-400 truncate">{placeholder}</span></>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-[70] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-2xl flex flex-col overflow-hidden" data-testid={`${testId}-panel`}>
                    <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKey}
                            placeholder="Search: OM, Oman, GST, Dubai…" aria-label="Search country or time zone"
                            data-testid={`${testId}-search`} className="w-full h-9 text-base sm:text-sm outline-none bg-transparent placeholder:text-slate-400" />
                    </div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400 border-b border-slate-50" data-testid={`${testId}-count`}>
                        {results.length} {results.length === 1 ? "location" : "locations"}
                    </div>
                    <div ref={listRef} role="listbox" className="overflow-y-auto overscroll-contain max-h-[min(18rem,45vh)]">
                        {results.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid={`${testId}-empty`}>No matching country, city or time zone</div>}
                        {results.map((o, i) => (
                            <div key={o.value}>
                                {pinned.length > 0 && (i === 0 || i === pinned.length) && (
                                    <div className={`px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400 ${i ? "border-t border-slate-100" : ""}`}
                                        data-testid={`${testId}-group-${i === 0 ? "popular" : "all"}`}>
                                        {i === 0 ? "Popular" : "All locations"}
                                    </div>
                                )}
                                <Row o={o} idx={i} date={date} testId={testId} active={i === active} selected={o.value === value}
                                    onPick={() => pick(o)} onHover={() => setActive(i)} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
